import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import { locationsApi, type LocationMapData } from './api'
import { handleApiError } from '@/lib/api/axios'
import { useAuthStore } from '@/app/store/auth'
import type { Location, LocationCategory } from '@/types/entities'
import type { ListParams, PaginatedResponse } from '@/lib/api/types'

// Query keys
const EMPTY_PARAMS: ListParams = {}

export const locationKeys = {
    all: ['locations'] as const,
    lists: () => [...locationKeys.all, 'list'] as const,
    list: (params: ListParams) => [...locationKeys.lists(), params] as const,
    details: () => [...locationKeys.all, 'detail'] as const,
    detail: (id: number) => [...locationKeys.details(), id] as const,
}

// Locations hooks
export function useLocations(params?: ListParams) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery<PaginatedResponse<Location>>({
        queryKey: locationKeys.list(params || EMPTY_PARAMS),
        queryFn: () => locationsApi.list(params),
        select: (response) => response.data,
        enabled: isAuthenticated && !mfaRequired,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
    })
}

export function useLocation(id: number) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery<Location>({
        queryKey: locationKeys.detail(id),
        queryFn: () => locationsApi.get(id),
        select: (response) => response.data,
        enabled: !!id && isAuthenticated && !mfaRequired,
    })
}

export function useCreateLocation() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: (data: Omit<Location, 'id'>) => locationsApi.create(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: locationKeys.all })
            toast({
                title: 'Success',
                description: 'Location created successfully',
            })
        },
        onError: (error) => {
            handleApiError(error, toast, 'Failed to create location')
        },
    })
}

export function useUpdateLocation() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: ({ id, data }: { id: number; data: Partial<Omit<Location, 'id'>> }) =>
            locationsApi.update(id, data),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: locationKeys.all })
            queryClient.invalidateQueries({ queryKey: locationKeys.detail(variables.id) })
            toast({
                title: 'Success',
                description: 'Location updated successfully',
            })
        },
        onError: (error) => {
            handleApiError(error, toast, 'Failed to update location')
        },
    })
}

export function useDeleteLocation() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: (id: number) => locationsApi.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: locationKeys.all })
            toast({
                title: 'Success',
                description: 'Location deleted successfully',
            })
        },
        onError: (error) => {
            handleApiError(error, toast, 'Failed to delete location')
        },
    })
}

/** Every country, for a country location. Only fetched while it is needed. */
export function useLocationCountries(enabled: boolean = true) {
    return useQuery({
        queryKey: [...locationKeys.all, 'countries'],
        queryFn: async () => (await locationsApi.getCountries()).data.data ?? [],
        enabled,
        staleTime: 60 * 60 * 1000,
    })
}

export function useLocationCategories(enabled: boolean = true) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery<LocationCategory[]>({
        queryKey: [...locationKeys.all, 'categories'],
        queryFn: () => locationsApi.getCategories(),
        select: (response) => {
            // Backend returns { success: true, data: [...], message: '...', timestamp: '...' }
            // Axios wraps it in { data: { success: true, data: [...], ... } }
            // So we need response.data.data to get the actual array
            const backendResponse = response.data
            return Array.isArray(backendResponse?.data) ? backendResponse.data : []
        },
        enabled: enabled && isAuthenticated && !mfaRequired,
        staleTime: 10 * 60 * 1000, // 10 minutes - categories don't change often
        refetchOnWindowFocus: false, // Prevent refetch on window focus
        refetchOnMount: false, // Prevent refetch on mount if data is fresh
        retry: 1, // Only retry once on failure
    })
}

export function useLocationMapData(enabled: boolean = true) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery<LocationMapData[]>({
        queryKey: [...locationKeys.all, 'map-data'],
        queryFn: () => locationsApi.getMapData(),
        select: (response) => {
            const backendResponse = response.data
            return Array.isArray(backendResponse?.data) ? backendResponse.data : []
        },
        enabled: enabled && isAuthenticated && !mfaRequired,
        staleTime: 5 * 60 * 1000, // 5 minutes
        refetchOnWindowFocus: false,
    })
}

export function useGoogleMapsApiKey(enabled: boolean = true) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
    const mfaRequired = useAuthStore((state) => state.mfaRequired)

    return useQuery<string>({
        queryKey: [...locationKeys.all, 'map-api-key'],
        queryFn: async () => {
            const response = await locationsApi.getMapApiKey()
            const backendResponse = response.data
            return backendResponse?.data?.apiKey || ''
        },
        enabled: enabled && isAuthenticated && !mfaRequired,
        staleTime: 60 * 60 * 1000, // 1 hour - API key doesn't change often
        refetchOnWindowFocus: false,
    })
}

